import { useRerender } from "@react-hookz/web";
import {
  DataTable as PrimeDataTable,
  type DataTableColumnResizeEndEvent,
  type DataTableProps as PrimeDataTableProps,
  type DataTableUnselectEvent as PrimeDataTableUnselectEvent,
} from "primereact/datatable";
import { useCallback, useEffect, useRef } from "react";
import styled, { css } from "styled-components";

import { TABLE_AUTOUPDATE_TIMEOUT } from "@ds/constants/table";

import { CheckBoxStylesMixin } from "../forms/inputs";
import { TruncatedCellTooltip } from "./truncated-cell-tooltip";

export type {
  DataTableRowReorderEvent,
  DataTableSelectEvent,
  DataTableSelectionChangeEvent,
  DataTableSortEvent,
  DataTableUnselectEvent,
} from "primereact/datatable";

export type DataTableProps<T extends Entity> = PrimeDataTableProps<T[]> & {
  onSingleRowSelect?: (value: T) => void;
  toggleRowSelected?: (value: T) => void;
};

const RowStyles = css`
  width: 100%;

  &.dimmed {
    color: var(--textColor040);
  }
`;

const CellStyles = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  min-width: var(--column-min-width);

  border: 0;

  .p-checkbox-box {
    box-shadow: 0 4px 6px var(--primaryColor015);

    &::after {
      content: "";
      background: transparent;

      position: absolute;
      padding: 120%;
    }
  }

  &.center {
    text-align: center;
  }

  &.w100 {
    width: 100px;
    min-width: 100px;
  }

  &.w120 {
    width: 120px;
    min-width: 120px;
  }

  &.w130 {
    width: 130px;
    min-width: 130px;
  }

  &.w140 {
    width: 140px;
    min-width: 140px;
  }

  &.w150 {
    width: 150px;
    min-width: 150px;
  }

  &.w160 {
    width: 160px;
    min-width: 160px;
  }

  &.w175 {
    width: 175px;
    min-width: 175px;
  }

  &.w200 {
    width: 200px;
    min-width: 200px;
  }
`;

const ReorderColumnStyle = css`
  > tr > .reorder-column {
    width: var(--icon-cell-width) !important;
    min-width: var(--icon-cell-width) !important;
    text-align: center;

    > span,
    svg,
    i {
      text-align: center;
      vertical-align: middle;
    }
  }
`;

const CheckboxColumnStyles = css`
  > tr {
    > .select-checkbox-column .p-checkbox:not(.p-multistatecheckbox) {
      display: none;
    }

    > .p-selection-column {
      width: var(--icon-cell-width) !important;
      min-width: var(--icon-cell-width) !important;
      text-align: center;
    }

    .p-checkbox {
      ${CheckBoxStylesMixin}
    }
  }
`;

const StatusColumnStyle = css`
  > tr > .status-column {
    width: var(--fixed-column-width) !important;
    min-width: var(--fixed-column-width) !important;
  }
`;

const LastUpdatedColumnStyles = css`
  > tr > .last_updated_column {
    width: var(--fixed-column-width) !important;
    min-width: var(--fixed-column-width) !important;
  }
`;

const GoToDetailsColumnStyles = css`
  > tr {
    > .go_to_details_column {
      width: var(--icon-cell-width) !important;
      min-width: var(--icon-cell-width) !important;
      text-align: center;

      a {
        visibility: hidden;
        opacity: 0;
        transition:
          visibility 0s linear 300ms,
          opacity 300ms;
      }
    }

    &:hover {
      .go_to_details_column a {
        visibility: visible;
        opacity: 1;
        transition:
          visibility 0s linear 0s,
          opacity 300ms;
      }
    }
  }
`;

const DataTableWrapper = styled.div`
  --horizontal-padding: 3px;
  --cell-padding: 6px;
  --icon-cell-width: 52px;
  --fixed-column-width: 175px;
  --column-min-width: 120px;
  --header-height: 52px;

  display: contents;

  .p-datatable {
    user-select: none;

    &.p-datatable-resizable > .p-datatable-wrapper {
      scrollbar-gutter: stable;
    }

    > .p-datatable-wrapper > table.p-datatable-table {
      /* max-content lets column min-widths expand the table past the viewport;
         min-width: 100% still fills the container when columns fit. */
      width: max-content;
      border-collapse: collapse;
      min-width: 100%;
      table-layout: fixed;

      thead.p-datatable-thead {
        z-index: 1;

        ${ReorderColumnStyle}
        ${CheckboxColumnStyles}
        ${StatusColumnStyle}
        ${LastUpdatedColumnStyles}
        ${GoToDetailsColumnStyles}

        > tr {
          ${RowStyles}
          height: var(--header-height);
        }

        > tr > th {
          ${CellStyles}
          /* .p-column-resizer is position:absolute at right:0 on the preceding column
             (PrimeReact base CSS). Without left padding the header text of each column
             starts flush against the resize handle of the column to its left. */
          padding-left: var(--cell-padding);
          padding-right: 0;

          &:first-child {
            padding-left: var(--horizontal-padding);
          }

          &.p-resizable-column {
            padding-right: var(--cell-padding);
          }

          /* After .p-resizable-column so a resizable last column keeps the tighter end padding. */
          &:last-child {
            padding-right: var(--horizontal-padding);
          }

          border: 0;
          box-shadow: var(--button-light-box-shadow);
          height: var(--header-height);
          color: var(--textColor060);

          .p-column-header-content {
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          &.p-sortable-column {
            &:not(.p-highlight):hover,
            &:not(.p-highlight):hover .p-sortable-column-icon {
              color: var(--textColor080);
            }

            .p-sortable-column-icon {
              color: var(--textColor060);
            }

            :not(.p-highlight):hover {
              background: inherit;
            }
          }

          .p-column-resizer {
            width: 5px;
            border-right: 1px dashed var(--blackShadowColor025);
            height: 25px;
            margin: 15px 0;
          }

          :not(.p-resizable-column) .p-column-resizer {
            display: none;
          }
        }
      }

      tbody.p-datatable-tbody {
        > tr {
          ${RowStyles}
          color: var(--textColor070);
          font-weight: 400;

          &.p-highlight {
            --row-border-color: var(--primaryColor080);
            color: var(--textColor070);
            font-weight: 600;
            background: linear-gradient(0deg, var(--primaryColor005), var(--primaryColor005)),
              var(--whiteShadowColor100);
            box-shadow: 0 4px 16px var(--primaryColor015);

            a {
              :hover {
                font-weight: 800;
              }

              i {
                :hover:before {
                  font-weight: 800;
                }
              }
            }
          }

          :not(.p-datatable-emptymessage) {
            :not(.p-highlight):hover {
              --row-border-color: var(--primaryColor030);
              color: var(--textColor080);
              background: var(--lightSurfaceColor);
            }

            :hover {
              box-shadow: 0 4px 16px var(--primaryColor015);
            }

            :active {
              --row-border-color: var(--primaryColor080);
              color: var(--primaryColor100);
              background: linear-gradient(0deg, var(--primaryColor005), var(--primaryColor005)),
                var(--whiteShadowColor100);
              box-shadow: 0 4px 16px var(--primaryColor015);
            }
          }

          &.p-datatable-dragpoint-bottom {
            td {
              box-shadow: none;
            }

            :not(.p-highlight) {
              td {
                box-shadow: inset 0 -4px 0 0 var(--primaryColor100);
              }

              /* Same value as td above; beats the row-border :first-child/:last-child rules. */
              td:first-child,
              td:last-child {
                box-shadow: inset 0 -4px 0 0 var(--primaryColor100);
              }
            }
          }

          &.p-datatable-dragpoint-top {
            td {
              box-shadow: none;
            }

            :not(.p-highlight) {
              td {
                box-shadow: inset 0 4px 0 0 var(--primaryColor100);
              }

              /* Same value as td above; beats the row-border :first-child/:last-child rules. */
              td:first-child,
              td:last-child {
                box-shadow: inset 0 4px 0 0 var(--primaryColor100);
              }
            }
          }
        }

        /* Expansion rows need their own styling context: CellStyles (nowrap/hidden/ellipsis)
           and the padding/box-shadow rules must not bleed into the expansion td, which uses
           colSpan and needs full-table-width display:table-cell layout. */
        > tr.p-datatable-row-expansion > td {
          border: 0;
        }

        > tr:not(.p-datatable-row-expansion) > td {
          ${CellStyles}
          padding-left: var(--cell-padding);
          padding-right: 0;
          box-shadow:
            inset 0 2px 0 0 var(--row-border-color, transparent),
            inset 0 -2px 0 0 var(--row-border-color, transparent);

          &:first-child {
            padding-left: var(--horizontal-padding);
            border-radius: 4px 0 0 4px;
            box-shadow:
              inset 2px 0 0 0 var(--row-border-color, transparent),
              inset 0 2px 0 0 var(--row-border-color, transparent),
              inset 0 -2px 0 0 var(--row-border-color, transparent);
          }

          &:last-child {
            padding-right: var(--horizontal-padding);
            border-radius: 0 4px 4px 0;
            box-shadow:
              inset -2px 0 0 0 var(--row-border-color, transparent),
              inset 0 2px 0 0 var(--row-border-color, transparent),
              inset 0 -2px 0 0 var(--row-border-color, transparent);
          }
        }

        ${ReorderColumnStyle}
        ${CheckboxColumnStyles}
        ${StatusColumnStyle}
        ${LastUpdatedColumnStyles}
        ${GoToDetailsColumnStyles}
      }
    }

    &.playlist-assets-table td {
      line-height: 100px;
    }
  }
`;

export const DataTable = <T extends Entity>({
  dataKey = "id",
  loading,
  toggleRowSelected,
  onSingleRowSelect,
  selectionMode = "multiple",
  removableSort = true,
  columnResizeMode = "fit",
  resizableColumns = true,
  onColReorder,
  onRowReorder,
  rowClassName,
  onColumnResizeEnd,
  ...rest
}: DataTableProps<T>) => {
  const rerender = useRerender();
  const timeout = useRef<NodeJS.Timeout>();
  useEffect(() => {
    timeout.current = setInterval(rerender, TABLE_AUTOUPDATE_TIMEOUT);

    return () => {
      if (timeout.current) {
        clearInterval(timeout.current);
      }
    };
  }, [rerender]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef(false);

  // Workaround for PrimeReact bug: `elementRef.current.setAttribute(attributeSelector.current, '')`
  // is only called in useUpdateEffect([props.breakpoint]), which skips mount in production builds
  // (no React Strict Mode double-invocation). The resize CSS selector `.p-datatable[pr_id_N]` never
  // matches because the attribute is never set, so injected column widths have no effect.
  //
  // Coupled to PrimeReact's call order in resizeTableCells: it appends the <style>, sets
  // innerHTML synchronously, then invokes props.onColumnResizeEnd. That queues our
  // Promise.resolve().then reset after the MutationObserver microtask, so the observer still
  // sees resizingRef === true for this table's injection.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const observer = new MutationObserver(mutations => {
      if (!resizingRef.current) return;
      const dtEl = wrapper.querySelector<HTMLElement>(".p-datatable");
      if (!dtEl) return;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLStyleElement)) continue;
          const match = node.textContent?.match(/\.p-datatable\[([^\]]+)\]/);
          if (!match) continue;
          const attrName = match[1];
          if (!dtEl.hasAttribute(attrName)) {
            dtEl.setAttribute(attrName, "");
          }
        }
      }
    });

    observer.observe(document.head, { childList: true });
    return () => observer.disconnect();
  }, []);

  const handleColumnResizeEnd = useCallback(
    (event: DataTableColumnResizeEndEvent) => {
      // Set the flag before the MutationObserver microtask fires so it can identify
      // that the next style injection belongs to this table's resize.
      resizingRef.current = true;
      Promise.resolve().then(() => {
        resizingRef.current = false;
      });
      onColumnResizeEnd?.(event);
    },
    [onColumnResizeEnd],
  );

  const onRowSelectHandler = useCallback(
    ({ originalEvent, type, data }: PrimeDataTableUnselectEvent) => {
      if (data) {
        if (!originalEvent.isPropagationStopped()) {
          if (type === "checkbox") {
            toggleRowSelected?.(data);
          } else {
            onSingleRowSelect?.(data);
          }
        }
      }

      originalEvent.stopPropagation();
    },
    [toggleRowSelected, onSingleRowSelect],
  );

  return (
    <DataTableWrapper ref={wrapperRef}>
      <TruncatedCellTooltip wrapperRef={wrapperRef} />
      <PrimeDataTable
        dataKey={dataKey}
        emptyMessage={loading ? "Loading entities..." : "No entities found"}
        onRowSelect={onRowSelectHandler}
        onRowUnselect={onRowSelectHandler}
        selectionMode={selectionMode}
        removableSort={removableSort}
        onRowReorder={onRowReorder}
        reorderableRows={!!onRowReorder}
        onColReorder={onColReorder}
        reorderableColumns={!!onColReorder}
        columnResizeMode={columnResizeMode}
        resizableColumns={resizableColumns}
        rowClassName={rowClassName}
        onColumnResizeEnd={handleColumnResizeEnd}
        scrollHeight="flex"
        scrollable
        {...rest}
      />
    </DataTableWrapper>
  );
};
